import { Calendar, ChevronDown, ChevronUp, Pill, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { cn, formatDate, humanize } from '@/lib/utils'
import { Badge } from '@/components/common/Badge'

const HIDE_KEYS = new Set(['id', 'visit_id', 'created_at', 'updated_at'])

function Field({ label, children }) {
  if (children == null || children === '' || (Array.isArray(children) && !children.length))
    return null
  return (
    <div>
      <p className="label-text mb-0.5">{label}</p>
      <div className="text-sm font-medium text-ink-800">{children}</div>
    </div>
  )
}

function ListOrDash({ items, transform = humanize }) {
  if (!items || items.length === 0) return <span className="text-ink-400">—</span>
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((it) => (
        <Badge key={String(it)} tone="primary">{transform(it)}</Badge>
      ))}
    </div>
  )
}

function renderGradedTeeth(graded, grades) {
  if (!graded?.present) {
    return <span className="text-ink-400">Not present</span>
  }
  const rows = grades
    .map((g) => {
      const data = graded[g]
      const a = data?.adult_teeth || []
      const c = data?.child_teeth || []
      if (!a.length && !c.length) return null
      return (
        <div key={g} className="flex flex-wrap items-center gap-2">
          <span className="rounded-md bg-ink-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-ink-700">
            Grade {g.split('_')[1]}
          </span>
          {a.length > 0 && (
            <span className="text-xs text-ink-700">
              Adult: <span className="font-semibold text-ink-900">{a.join(', ')}</span>
            </span>
          )}
          {c.length > 0 && (
            <span className="text-xs text-ink-700">
              Child: <span className="font-semibold text-ink-900">{c.join(', ')}</span>
            </span>
          )}
        </div>
      )
    })
    .filter(Boolean)
  return rows.length === 0 ? (
    <span className="text-ink-400">Present, no teeth recorded</span>
  ) : (
    <div className="space-y-1.5">{rows}</div>
  )
}

function Section({ title, children }) {
  if (!children) return null
  return (
    <div className="rounded-xl bg-ink-50/40 p-4 ring-1 ring-ink-100">
      <h4 className="mb-3 text-xs font-bold uppercase tracking-[0.12em] text-primary-700">
        {title}
      </h4>
      <div className="space-y-3">{children}</div>
    </div>
  )
}

export function VisitDetailsView({ visit, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen)
  if (!visit) return null

  const {
    visit_id,
    visit_date,
    visit_number,
    chief_complaint,
    history_present_illness,
    medical_history,
    personal_history,
    vitals,
    dental_history,
    extra_oral_examination,
    intra_oral_examination,
    investigation,
    provisional_diagnosis,
    treatment_plan,
    prescription,
    consent,
    doctor_notes,
  } = visit

  return (
    <div className="rounded-2xl bg-white shadow-soft ring-1 ring-ink-100">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-800 ring-1 ring-primary-100">
            <Calendar className="h-5 w-5" />
          </div>
          <div>
            <p className="h-display text-base font-semibold text-ink-900">
              Visit #{visit_number}
            </p>
            <p className="text-xs text-ink-500">
              {formatDate(visit_date)} · ID {visit_id}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {chief_complaint?.complaint_text && (
            <Badge tone="accent" className="hidden sm:inline-flex">Has complaint</Badge>
          )}
          {prescription?.medicines?.length > 0 && (
            <Badge tone="primary" leftIcon={<Pill className="h-3 w-3" />}>
              {prescription.medicines.length} med{prescription.medicines.length > 1 ? 's' : ''}
            </Badge>
          )}
          {open ? (
            <ChevronUp className="h-4 w-4 text-ink-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-ink-500" />
          )}
        </div>
      </button>

      {open && (
        <div className="space-y-4 border-t border-ink-100 px-5 py-5 animate-fade-in">
          <Section title="Chief Complaint">
            {chief_complaint?.complaint_text ? (
              <p className="text-sm text-ink-800">{chief_complaint.complaint_text}</p>
            ) : null}
          </Section>

          <Section title="History of Present Illness">
            {history_present_illness ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Duration">{history_present_illness.duration}</Field>
                <Field label="Type of pain">
                  <ListOrDash items={history_present_illness.type_of_pain} />
                </Field>
                <Field label="Aggravating factors">{history_present_illness.aggravating_factors}</Field>
                <Field label="Relieving factors">{history_present_illness.relieving_factors}</Field>
                <Field label="Previous treatment">{history_present_illness.previous_dental_treatment}</Field>
                <Field label="Swelling / fever / pus">
                  <ListOrDash items={history_present_illness.swelling_fever_pus} />
                </Field>
              </div>
            ) : null}
          </Section>

          <Section title="Medical History">
            {medical_history ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Conditions">
                  <ListOrDash items={medical_history.conditions} />
                </Field>
                <Field label="Allergies">{medical_history.allergies}</Field>
                <Field label="Current medications">{medical_history.current_medications}</Field>
                <Field label="Previous surgeries">{medical_history.previous_surgeries}</Field>
              </div>
            ) : null}
          </Section>

          <Section title="Personal History">
            {personal_history ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Habits">
                  <ListOrDash items={personal_history.habits} />
                </Field>
                <Field label="Diet">{personal_history.diet && humanize(personal_history.diet)}</Field>
                <Field label="Brushing frequency">{personal_history.brushing_frequency}</Field>
                <Field label="Mouthwash / floss">
                  <ListOrDash items={personal_history.mouthwash_floss} />
                </Field>
              </div>
            ) : null}
          </Section>

          <Section title="Vitals">
            {vitals ? (
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="BP">{vitals.blood_pressure}</Field>
                <Field label="Pulse">{vitals.pulse_rate}</Field>
                <Field label="Resp. rate">{vitals.respiratory_rate}</Field>
                <Field label="Temperature">{vitals.temperature}</Field>
                <Field label="SpO₂">{vitals.spo2}</Field>
                <Field label="RBS">{vitals.random_blood_sugar}</Field>
              </div>
            ) : null}
          </Section>

          <Section title="Dental History">
            {dental_history ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Previous extraction">{dental_history.previous_extraction}</Field>
                <Field label="Root canal treatment">{dental_history.root_canal_treatment}</Field>
                <Field label="Orthodontic treatment">{dental_history.orthodontic_treatment}</Field>
                <Field label="Denture / prosthesis">{dental_history.denture_prosthesis}</Field>
                <Field label="Last dental visit">{dental_history.last_dental_visit}</Field>
              </div>
            ) : null}
          </Section>

          <Section title="Extra Oral Examination">
            {extra_oral_examination ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Facial symmetry">{extra_oral_examination.facial_symmetry}</Field>
                <Field label="Swelling">{extra_oral_examination.swelling}</Field>
                <Field label="Lymph nodes">{extra_oral_examination.lymph_nodes}</Field>
                <Field label="TMJ findings">{extra_oral_examination.tmj_findings}</Field>
                <Field label="Mouth opening">{extra_oral_examination.mouth_opening}</Field>
              </div>
            ) : null}
          </Section>

          <Section title="Intra Oral Examination">
            {intra_oral_examination ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Oral hygiene">
                  {intra_oral_examination.oral_hygiene_status &&
                    humanize(intra_oral_examination.oral_hygiene_status)}
                </Field>
                <Field label="Gingiva">
                  {intra_oral_examination.gingiva && humanize(intra_oral_examination.gingiva)}
                </Field>
                <Field label="Recession">
                  {intra_oral_examination.recession && humanize(intra_oral_examination.recession)}
                </Field>
                <Field label="Calculus / stains">
                  {intra_oral_examination.calculus_stains && `Grade ${intra_oral_examination.calculus_stains}`}
                </Field>
                <Field label="Caries">
                  {renderGradedTeeth(intra_oral_examination.caries, [
                    'grade_1', 'grade_2', 'grade_3', 'grade_4', 'grade_5', 'grade_6',
                  ])}
                </Field>
                <Field label="Tooth mobility">
                  {renderGradedTeeth(intra_oral_examination.tooth_mobility, [
                    'grade_1', 'grade_2', 'grade_3',
                  ])}
                </Field>
                <Field label="Missing teeth">
                  {(() => {
                    const a = intra_oral_examination.missing_teeth?.adult_teeth || []
                    const c = intra_oral_examination.missing_teeth?.child_teeth || []
                    if (!a.length && !c.length) return null
                    return (
                      <div className="space-y-1 text-xs">
                        {a.length > 0 && (
                          <span className="block">Adult: <span className="font-semibold">{a.join(', ')}</span></span>
                        )}
                        {c.length > 0 && (
                          <span className="block">Child: <span className="font-semibold">{c.join(', ')}</span></span>
                        )}
                      </div>
                    )
                  })()}
                </Field>
                <Field label="Soft tissue findings">{intra_oral_examination.soft_tissue_findings}</Field>
                <Field label="Occlusion">{intra_oral_examination.occlusion}</Field>
              </div>
            ) : null}
          </Section>

          <Section title="Investigation">
            {investigation ? (
              <div className="space-y-3">
                <Field label="Tests advised">
                  <ListOrDash items={investigation.investigations} transform={(v) => v.toUpperCase()} />
                </Field>
                <Field label="Notes">{investigation.notes}</Field>
              </div>
            ) : null}
          </Section>

          <Section title="Provisional Diagnosis">
            {provisional_diagnosis?.diagnosis_text ? (
              <p className="text-sm text-ink-800">{provisional_diagnosis.diagnosis_text}</p>
            ) : null}
          </Section>

          <Section title="Treatment Plan">
            {treatment_plan ? (
              <div className="space-y-3">
                <Field label="Treatments">
                  <ListOrDash items={treatment_plan.treatments} />
                </Field>
                <Field label="Notes">
                  {treatment_plan.plan_text && (
                    <p className="whitespace-pre-line text-sm">{treatment_plan.plan_text}</p>
                  )}
                </Field>
              </div>
            ) : null}
          </Section>

          {prescription?.medicines?.length > 0 && (
            <Section title="Prescription">
              <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-ink-100">
                <table className="w-full text-sm">
                  <thead className="bg-ink-50/60">
                    <tr>
                      <th className="px-3 py-2 text-left label-text">Medicine</th>
                      <th className="px-3 py-2 text-left label-text">Dosage</th>
                      <th className="px-3 py-2 text-left label-text">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prescription.medicines.map((m, i) => (
                      <tr key={i} className="border-t border-ink-100">
                        <td className="px-3 py-2 font-medium text-ink-900">{m.medicine}</td>
                        <td className="px-3 py-2 text-ink-700">{m.dosage}</td>
                        <td className="px-3 py-2 text-ink-700">{m.duration}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {consent && (consent.written_consent_obtained || consent.procedure_explained || consent.risks_explained) && (
            <Section title="Consent">
              <div className="flex flex-wrap gap-2">
                {consent.procedure_explained && <Badge tone="success" leftIcon={<ShieldCheck className="h-3 w-3" />}>Procedure explained</Badge>}
                {consent.risks_explained && <Badge tone="success" leftIcon={<ShieldCheck className="h-3 w-3" />}>Risks explained</Badge>}
                {consent.written_consent_obtained && <Badge tone="success" leftIcon={<ShieldCheck className="h-3 w-3" />}>Written consent</Badge>}
              </div>
            </Section>
          )}

          <Section title="Doctor Notes">
            {doctor_notes ? (
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Special precautions">{doctor_notes.special_precautions}</Field>
                <Field label="Follow-up advice">{doctor_notes.follow_up_advice}</Field>
                <Field label="Emergency instructions">{doctor_notes.emergency_instructions}</Field>
              </div>
            ) : null}
          </Section>
        </div>
      )}
    </div>
  )
}
